import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// Initial State
const initialState = {
    subSessionMasters: [],
    currentSubSessionMaster: null,
    isLoading: false,
    error: null,
};

/* ==============================
   ASYNC THUNKS
============================== */

// Fetch all sub session masters
export const fetchSubSessionMasters = createAsyncThunk(
    'subSession/fetchSubSessionMasters',
    async (params, { rejectWithValue }) => {
        try {
            const response = await api.get('/sub-session-masters', { params });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch sub session types');
        }
    }
);

// Fetch single sub session master by ID
export const fetchSubSessionMaster = createAsyncThunk(
    'subSession/fetchSubSessionMaster',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/sub-session-masters/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch sub session type details');
        }
    }
);

// Create new sub session master
export const createSubSessionMaster = createAsyncThunk(
    'subSession/createSubSessionMaster',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await api.post('/sub-session-masters', formData);
            return response.data.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(error.response?.data?.message || 'Failed to create sub session type');
        }
    }
);

// Update sub session master
export const updateSubSessionMaster = createAsyncThunk(
    'subSession/updateSubSessionMaster',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/sub-session-masters/${id}`, formData);
            return response.data.data ?? response.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(error.response?.data?.message || 'Failed to update sub session type');
        }
    }
);

// Delete sub session master
export const deleteSubSessionMaster = createAsyncThunk(
    'subSession/deleteSubSessionMaster',
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/sub-session-masters/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete sub session type');
        }
    }
);

/* ==============================
   SLICE
============================== */

const subSessionSlice = createSlice({
    name: 'subSessions',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setCurrentSubSessionMaster: (state, action) => {
            state.currentSubSessionMaster = action.payload;
        },
        clearCurrentSubSessionMaster: (state) => {
            state.currentSubSessionMaster = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // FETCH ALL
            .addCase(fetchSubSessionMasters.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSubSessionMasters.fulfilled, (state, action) => {
                state.isLoading = false;
                state.subSessionMasters = action.payload;
            })
            .addCase(fetchSubSessionMasters.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // FETCH SINGLE
            .addCase(fetchSubSessionMaster.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSubSessionMaster.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentSubSessionMaster = action.payload;
            })
            .addCase(fetchSubSessionMaster.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // CREATE
            .addCase(createSubSessionMaster.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createSubSessionMaster.fulfilled, (state, action) => {
                state.isLoading = false;
                if (!action.payload) return;
                state.subSessionMasters.unshift(action.payload);
                state.currentSubSessionMaster = action.payload;
            })
            .addCase(createSubSessionMaster.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // UPDATE
            .addCase(updateSubSessionMaster.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateSubSessionMaster.fulfilled, (state, action) => {
                state.isLoading = false;
                const updatedData = action.payload.data || action.payload;
                if (!updatedData) return;

                const index = state.subSessionMasters.findIndex((f) => f.id === updatedData.id);
                if (index !== -1) {
                    state.subSessionMasters[index] = updatedData;
                }
                if (state.currentSubSessionMaster?.id === updatedData.id) {
                    state.currentSubSessionMaster = updatedData;
                }
            })
            .addCase(updateSubSessionMaster.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // DELETE
            .addCase(deleteSubSessionMaster.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteSubSessionMaster.fulfilled, (state, action) => {
                state.isLoading = false;
                state.subSessionMasters = state.subSessionMasters.filter((f) => f.id !== action.payload);
                if (state.currentSubSessionMaster?.id === action.payload) {
                    state.currentSubSessionMaster = null;
                }
            })
            .addCase(deleteSubSessionMaster.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearError, setCurrentSubSessionMaster, clearCurrentSubSessionMaster } = subSessionSlice.actions;
export default subSessionSlice.reducer;
